import { Card } from 'components/Card';
import { Icon, ICONS } from 'components/Icon';
import { Txt } from 'components/Txt';
import { UI_DATE_FORMAT_LONG } from 'lib/constants';
import { theme } from 'lib/theme';
import React from 'react';
import { View } from 'react-native';
import { format, max, subDays } from 'date-fns';
import {
  EngageScheduledVisitStatus,
  ScheduledVisitCommunicationSchedule,
  ScheduledVisitMessageTemplate,
} from 'types/DTOs/BlockManagerDTOs';
import { CardRow } from 'components/CardRow';
import { ActivityIndicator } from 'components/BlockingLoader/ActivityIndicator';
import { filterArrayEmpty } from 'lib/riskbase';

type EngageScheduledVisitTimelineProps = {
  templates: ScheduledVisitMessageTemplate[];
  communicationSchedule: ScheduledVisitCommunicationSchedule[];
  scheduledVisitStatus?: EngageScheduledVisitStatus;
  earliestSelectedDate?: Date | null;
  publishedDate?: Date | null;
  loading: boolean;
};

const CANCELLATION_TEMPLATE_ID =
  'door_check_scheduled_visit_cancellation_email';

export const EngageScheduledVisitTimeline: React.FC<
  EngageScheduledVisitTimelineProps
> = ({
  templates,
  communicationSchedule,
  scheduledVisitStatus,
  earliestSelectedDate,
  publishedDate,
  loading,
}) => {
  const [isOpen, setIsOpen] = React.useState<Record<string, boolean>>({});
  const hideUnsentTemplates =
    scheduledVisitStatus === EngageScheduledVisitStatus.CANCELLED;

  // Only include the cancellation template if the visit is cancelled.
  // When cancelled, hide unsent non-cancellation templates.
  const timelineTemplates = React.useMemo(
    () =>
      templates.filter((template) => {
        const isCancellationTemplate = template.id === CANCELLATION_TEMPLATE_ID;
        const communication = communicationSchedule.find(
          (item) =>
            item.templateType.toLowerCase() === template.name.toLowerCase(),
        );
        const hasDisplayDate = !!(
          communication?.failedDate || communication?.sentDate
        );

        return (
          (scheduledVisitStatus === EngageScheduledVisitStatus.CANCELLED ||
            !isCancellationTemplate) &&
          (!hideUnsentTemplates || isCancellationTemplate || hasDisplayDate)
        );
      }),
    [
      templates,
      communicationSchedule,
      scheduledVisitStatus,
      hideUnsentTemplates,
    ],
  );

  return (
    <>
      <Card label="Message Timeline" labelSize="large" margin={false}></Card>
      {loading && (
        <Card margin={false}>
          <CardRow contentStyle={{ paddingTop: 0, paddingBottom: 0 }}>
            <ActivityIndicator size={16} />
          </CardRow>
        </Card>
      )}
      {!loading &&
        timelineTemplates.map((template) => {
          const isCancellationTemplate =
            template.id === CANCELLATION_TEMPLATE_ID;
          // The send date is the latest of the following:
          // - x days before earliestSelectedDate or
          // - if published, the published date, otherwise the current date
          const sendDate = isCancellationTemplate
            ? null
            : earliestSelectedDate &&
              max(
                [
                  subDays(earliestSelectedDate, template.sendDays),
                  publishedDate || new Date(),
                ].filter(filterArrayEmpty),
              );
          const communication = communicationSchedule.find(
            (item) =>
              item.templateType.toLowerCase() === template.name.toLowerCase(),
          );
          const displayDate =
            communication?.failedDate || communication?.sentDate;
          const isFailed = !!communication?.failedDate;
          const isPreviewOpen = isOpen[template.name] ?? false;

          return (
            <Card key={template.id} marginBottomQuarter>
              <CardRow
                icon={ICONS.ChevronRightLight}
                iconSize={16}
                iconStyle={{
                  transform: [{ rotate: isPreviewOpen ? '90deg' : '0deg' }],
                }}
                onPress={() => {
                  setIsOpen((prev) => ({
                    ...prev,
                    [template.name]: !(prev[template.name] ?? false),
                  }));
                }}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <Txt
                    size="normal"
                    weight="medium"
                    style={{
                      flex: 1,
                    }}
                  >
                    {template.name}
                  </Txt>
                  {/* If we have a sent date, show it, if not show the send date */}
                  {displayDate ? (
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: theme.paddingHorizontalHalf,
                      }}
                    >
                      <Icon
                        source={
                          isFailed
                            ? ICONS.CircleXmarkLight
                            : ICONS.CircleCheckSolid
                        }
                        color={isFailed ? theme.colors.red : theme.colors.green}
                        size={20}
                      />
                      <Txt
                        size="small"
                        weight="medium"
                        style={{ color: theme.colors.fieldValueText }}
                      >
                        {`${isFailed ? 'Failed' : 'Sent'}: ${format(displayDate, UI_DATE_FORMAT_LONG)}`}
                      </Txt>
                    </View>
                  ) : (
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: theme.paddingHorizontalHalf,
                      }}
                    >
                      <Icon
                        source={ICONS.ClockLight}
                        color={theme.colors.fieldValueText}
                        size={20}
                      />
                      <Txt
                        size="small"
                        weight="medium"
                        style={{ color: theme.colors.fieldValueText }}
                      >
                        {sendDate
                          ? format(sendDate, UI_DATE_FORMAT_LONG)
                          : 'Pending'}
                      </Txt>
                    </View>
                  )}
                </View>
                <Txt style={{ marginTop: 2 }} size="xsmall">
                  {template.description}
                </Txt>
              </CardRow>
              {isPreviewOpen && (
                <CardRow>
                  <Txt weight="medium" size="small">
                    Subject: {template.subject}
                  </Txt>
                  <Txt size="small">{template.body}</Txt>
                </CardRow>
              )}
            </Card>
          );
        })}
    </>
  );
};
